import { Injectable } from '@angular/core';
import { HttpClient, HttpErrorResponse } from '@angular/common/http';

import { ApiBaseService } from './api-base.service';

import { SharedService } from './shared.service';
import { LoyaltyPlanSearchModel } from '@shared/models/SearchModel';
import { UnsubscribeLoyaltyPlanModel, CheckAutoAssignCodeModel, GetLoyaltyPlanCustomerModel, JoinLoyaltyPlanModel, LoyaltyPlanCustomerStickerModel, LoyaltyPlanModel, LoyaltyPlanLinkModel, AssignmentCodeWebInfoModel, ClaimAssignmentCodeModel, ClaimAssignmentCodeResultModel } from '@shared/models/LoyaltyPlanModel';
import { Country } from '@shared/models/Enums';
import { CouponModel } from '@shared/models/CouponModel';
import { ScratchModel } from '@shared/models/ScratchModel';
import { GiftcardModel } from '@shared/models/GiftcardModel';


@Injectable()
export class ApiLoyaltyPlanService extends ApiBaseService {

    constructor(public http: HttpClient, public sharedService: SharedService) {
        super(http, sharedService);
    }

    // POST: api/admin/loyaltyplans/reads/v1 
    getAllLoyaltyPlans(search: LoyaltyPlanSearchModel): Promise<LoyaltyPlanModel[]> {

        return this.http.post(`${this.baseUrl}/api/admin/loyaltyplans/reads/v1`, search, this.getHttpOptions())
            .toPromise().then(
                (results: any) => {

                    // Todas los planes 
                    let allLoyaltyPlans = results.map(x => {
                        return new LoyaltyPlanModel(x);
                    }).sort((a: LoyaltyPlanModel, b: LoyaltyPlanModel) => b.id - a.id);

                    return allLoyaltyPlans;
                },
                (err: HttpErrorResponse) => {

                    const errMsg = this.getErrorMsg(err);

                    return Promise.reject(new Error(`Error al cargar información de los planes de lealtad: ${errMsg}`));
                }
            );
    }

    // GET: api/admin/loyaltyplans/reads/v1/{id}
    getLoyaltyPlan(id: number, country?: Country): Promise<LoyaltyPlanModel> {

        return this.http.get(`${this.baseUrl}/api/admin/loyaltyplans/reads/v1/${id}`, this.getHttpOptions(country))
            .toPromise().then(
                (results: any) => {
                    
                    let plan = new LoyaltyPlanModel(results);

                    return plan;
                },
                (err: HttpErrorResponse) => {

                    const errMsg = this.getErrorMsg(err);

                    return Promise.reject(new Error(`Error al cargar información del plan de lealtad: ${errMsg}`));
                }
            );
    }

    // POST: api/admin/loyaltyplans/customersticker/last/reads/v1
    getLastSticker(getLast: GetLoyaltyPlanCustomerModel, country?: Country): Promise<LoyaltyPlanCustomerStickerModel> {

        return this.http.post(`${this.baseUrl}/api/admin/loyaltyplans/customersticker/last/reads/v1`, getLast, this.getHttpOptions(country))
            .toPromise().then(
                (results: any) => {

                    let plan = new LoyaltyPlanCustomerStickerModel(results);

                    return plan;
                },
                (err: HttpErrorResponse) => {

                    const errMsg = this.getErrorMsg(err);

                    return Promise.reject(new Error(`Error al cargar información del plan de lealtad: ${errMsg}`));
                }
            );
    }

    // POST: api/admin/loyaltyplans/customer/reads/v1/{id}
    getAllLoyaltyPlansByCustomer(id: number): Promise<LoyaltyPlanModel[]> {

        return this.http.post(`${this.baseUrl}/api/admin/loyaltyplans/customer/reads/v1/${id}`, null, this.getHttpOptions())
            .toPromise().then(
                (results: any) => {

                    // Todas los planes 
                    let allLoyaltyPlans = results.map(x => {
                        return new LoyaltyPlanModel(x);
                    }).sort((a: LoyaltyPlanModel, b: LoyaltyPlanModel) => b.id - a.id);

                    return allLoyaltyPlans;
                },
                (err: HttpErrorResponse) => {

                    const errMsg = this.getErrorMsg(err);

                    return Promise.reject(new Error(`Error al cargar información de los planes de lealtad: ${errMsg}`));
                }
            );
    }

    // POST: api/admin/loyaltyplans/customer/join/create/v1
    postJoinLoyaltyPlan(loyaltyPlanJoin: JoinLoyaltyPlanModel, country?: Country): Promise<any> {

        return this.http.post(`${this.baseUrl}/api/admin/loyaltyplans/customer/join/create/v1`, loyaltyPlanJoin, this.getHttpOptions(country))
            .toPromise();
    }

    // POST: api/admin/loyaltyplans/assignment/code/web/reads/v1
    postAssignmentCodeWebInfo(info: AssignmentCodeWebInfoModel, country?: Country): Promise<AssignmentCodeWebInfoModel> {

        return this.http.post(`${this.baseUrl}/api/admin/loyaltyplans/assignment/code/web/reads/v1`, info, this.getHttpOptionsWithoutSession(country))
            .toPromise().then((result: any) => new AssignmentCodeWebInfoModel(result));
    }

    // POST: api/admin/loyaltyplans/assignment/code/web/claim/v1
    postClaimAssignmentCodeWeb(claim: ClaimAssignmentCodeModel, country?: Country): Promise<ClaimAssignmentCodeResultModel> {

        return this.http.post(`${this.baseUrl}/api/admin/loyaltyplans/assignment/code/web/claim/v1`, claim, this.getHttpOptions(country))
            .toPromise().then((result: any) => result as ClaimAssignmentCodeResultModel);
    }

    // PUT: api/admin/loyaltyplans/customer/wallet-links/create/v1/{loyaltyPlanCustomerId}
    putGenerateWalletLinks(loyaltyPlanCustomerId: number, country?: Country): Promise<any> {

        return this.http.put(`${this.baseUrl}/api/admin/loyaltyplans/customer/wallet-links/create/v1/${loyaltyPlanCustomerId}`, null, this.getHttpOptions(country))
            .toPromise();
    }


    // POST: api/admin/loyaltyplans/scratch/reads/v1/{id}
    getScratchByLoyaltyPlan(id: number, country?: Country): Promise<ScratchModel> {

        return this.http.post(`${this.baseUrl}/api/admin/loyaltyplans/scratch/reads/v1/${id}`, null, this.getHttpOptions(country))
            .toPromise().then(
                (results: any) => {

                    let scratch = new ScratchModel(results);
                    return scratch;
                },
                (err: HttpErrorResponse) => {

                    const errMsg = this.getErrorMsg(err);

                    return Promise.reject(new Error(`Error al cargar información de la raspadita del plan de lealtad: ${errMsg}`));
                }
            );
    }

    // POST: api/admin/loyaltyplans/coupons/reads/v1/{id}
    getOffersByLoyaltyPlan(id: number, country?: Country): Promise<CouponModel[]> {

        return this.http.post(`${this.baseUrl}/api/admin/loyaltyplans/coupons/reads/v1/${id}`, null, this.getHttpOptions(country))
            .toPromise().then(
                (results: any) => {

                    let allOffers = results.map(x => {
                        return new CouponModel(x);
                    }).sort((a: CouponModel, b: CouponModel) => b.id - a.id);

                    return allOffers;
                },
                (err: HttpErrorResponse) => {

                    const errMsg = this.getErrorMsg(err);

                    return Promise.reject(new Error(`Error al cargar información de la raspadita del plan de lealtad: ${errMsg}`));
                }
            );
    }

    // POST: api/admin/loyaltyplans/giftcard/reads/v1/{id} 
    getPopByLoyaltyPlan(id: number, country?: Country): Promise<GiftcardModel> {

        return this.http.post(`${this.baseUrl}/api/admin/loyaltyplans/giftcard/reads/v1/${id}`, null, this.getHttpOptions(country))
            .toPromise().then(
                (results: any) => {

                    let pop = new GiftcardModel(results);
                    return pop;
                },
                (err: HttpErrorResponse) => {

                    const errMsg = this.getErrorMsg(err);

                    return Promise.reject(new Error(`Error al cargar información de la raspadita del plan de lealtad: ${errMsg}`));
                }
            );
    }

    // PUT: api/admin/loyaltyplans/customer/unsubscribe/update/v1
    unsubscribeLoyaltyPlan(unsubscribeLoyaltyPlan: UnsubscribeLoyaltyPlanModel, country: Country): Promise<any> {

        return this.http.put(`${this.baseUrl}/api/admin/loyaltyplans/customer/unsubscribe/update/v1`, unsubscribeLoyaltyPlan, this.getHttpOptions(country))
            .toPromise();
    }

    // POST: api/admin/loyaltyplans/customer/check-assign-code/reads/v1
    postCheckLinkAndLoyaltyPlansCustomer(checkAutoAssignCodeModel: CheckAutoAssignCodeModel, country?: Country): Promise<CheckAutoAssignCodeModel> {

        return this.http.post(`${this.baseUrl}/api/admin/loyaltyplans/customer/check-assign-code/reads/v1`, checkAutoAssignCodeModel, this.getHttpOptions(country))
            .toPromise().then(
                (result: any) => {

                    return result;
                },
                (err: HttpErrorResponse) => {

                    const errMsg = this.getErrorMsg(err);

                    return Promise.reject(new Error(errMsg));
                }
            );
    } 

    //PUT: api/admin/loyaltyplans/customersticker/claimed/update/v1/{customerStickerId}
    putCustomerStickerClaimed(customerStickerId): Promise<any> {

        return this.http.put(`${this.baseUrl}/api/admin/loyaltyplans/customersticker/claimed/update/v1/${customerStickerId}`, null, this.getHttpOptions())
            .toPromise();
    }

    // POST: api/admin/loyaltyplans/form/reads/v1/{id}
    getCheckoutFullLoyaltyForm(id: number, country: Country): Promise<LoyaltyPlanLinkModel> {
    
        return this.http.get(`${this.baseUrl}/api/admin/loyaltyplans/form/reads/v1/${id}`, this.getHttpOptionsWithoutSession(country))
            .toPromise().then(
                (result: any) => {

                    let fullForm = result;

                    return fullForm;
                },
                (err: HttpErrorResponse) => {

                    
                    console.log(err);
                    const errMsg = this.getErrorMsg(err);

                    return Promise.reject(new Error(`Error al cargar información : ${errMsg}`));
                }
            );
    }
    
}